import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { CostCentreService } from '../../services/costCentre.service';
import { AuthService } from '../../services/auth.service';
import { DepartmentPl } from '../../interfaces/costCentre.interface';
import DepartmentPicker, { useDepartmentOptions } from './DepartmentPicker';
import { useToast } from '../../context/ToastContext';
import { firstOfMonthLocalIso, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';

interface Applied {
    from: string;
    to: string;
    departmentId: string;
}

/** B4-H3: revenue and expense by department (cost centre), with an "Unassigned" bucket, and its Excel export. */
const DepartmentPlPage: React.FC = () => {
    const toast = useToast();
    const institution = AuthService.getSelectedInstitution();
    const departments = useDepartmentOptions(institution);
    const [from, setFrom] = useState(firstOfMonthLocalIso());
    const [to, setTo] = useState(toLocalIsoDate());
    const [departmentId, setDepartmentId] = useState('');
    const [applied, setApplied] = useState<Applied | null>(null);
    const [report, setReport] = useState<DepartmentPl | null>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const data = await CostCentreService.getDepartmentPl({ from, to, departmentId: departmentId || undefined });
            setReport(data);
            setApplied({ from, to, departmentId });
        } catch (err) {
            toast.showApiError(err, 'Failed to load the department P&L');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, departmentId]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The export carries the APPLIED filters, never unapplied form edits.
    const download = async () => {
        if (!applied) return;
        try {
            await CostCentreService.downloadDepartmentPl(
                { from: applied.from, to: applied.to, departmentId: applied.departmentId || undefined }, 'xlsx');
        } catch (err) {
            toast.showApiError(err, 'Download failed');
        }
    };

    return (
        <Box data-help-page="projects.deptPl.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Department P&L</Typography>
            <Box data-help-section="projects.deptPl.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 3 }}>
                <TextField data-help="projects.deptPl.from" label="From" type="date" size="small" value={from} onChange={e => setFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 190 }} />
                <TextField data-help="projects.deptPl.to" label="To" type="date" size="small" value={to} onChange={e => setTo(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 190 }} />
                <Box data-help="projects.deptPl.department" sx={{ flex: '1 1 240px', maxWidth: 360 }}>
                    <DepartmentPicker value={departmentId} onChange={setDepartmentId} options={departments}
                        label="Department" placeholder="All departments" data-testid="department-pl-department" />
                </Box>
                <Button data-help="projects.deptPl.apply" variant="contained" onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Apply'}</Button>
                <Button data-help="projects.deptPl.excel" variant="outlined" startIcon={<FileDownloadIcon />} onClick={download} disabled={!applied}>Excel</Button>
            </Box>

            {report && (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                        <Typography><strong>Revenue:</strong> {formatMoney(report.totalRevenue)}</Typography>
                        <Typography><strong>Expense:</strong> {formatMoney(report.totalExpense)}</Typography>
                        <Typography><strong>Profit:</strong> {formatMoney(report.totalProfit)}</Typography>
                    </Box>
                    {report.departments.length === 0 && (
                        <Typography color="text.secondary">No revenue or expense in this period.</Typography>
                    )}
                    {report.departments.map(block => (
                        <Paper key={block.departmentId ?? 'unassigned'} variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}
                            data-testid={`department-pl-block-${block.departmentId ?? 'unassigned'}`}>
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {block.name}{block.costCentreCode ? ` (${block.costCentreCode})` : ''}
                                </Typography>
                            </Box>
                            <Table data-help-section="projects.deptPl.table" size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell data-help="projects.deptPl.col.account">Account</TableCell>
                                        <TableCell data-help="projects.deptPl.col.revenue" align="right">Revenue</TableCell>
                                        <TableCell data-help="projects.deptPl.col.expense" align="right">Expense</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {block.accounts.map(a => (
                                        <TableRow key={a.accountId}>
                                            <TableCell>{a.accountCode ? `${a.accountCode} ` : ''}{a.accountName}</TableCell>
                                            <TableCell align="right">{a.accountType === 'REVENUE' ? formatMoney(a.amount) : ''}</TableCell>
                                            <TableCell align="right">{a.accountType === 'EXPENSE' ? formatMoney(a.amount) : ''}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(block.revenue)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(block.expense)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Profit</TableCell>
                                        <TableCell align="right" colSpan={2} sx={{ fontWeight: 600 }}>{formatMoney(block.profit)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Paper>
                    ))}
                    {report.notes.map(n => (
                        <Typography key={n} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{n}</Typography>
                    ))}
                </>
            )}
        </Box>
    );
};

export default DepartmentPlPage;
